import { FrontendUserCookieService } from './frontendUserCookieService';

interface RetryOptions {
  maxRetries?: number;
  initialDelayMs?: number;
  maxDelayMs?: number;
}

export interface EnsureUserIdOptions {
  maxRetries?: number;
  initialDelayMs?: number;
  maxDelayMs?: number;
  onRetrying?: (isRetrying: boolean) => void;
}

interface AuthCookieServiceProps {
  getLegacyUserId(): number;
  getUserId(): string | null;
  getAccountId(): number;
  waitForUserId(options?: { maxRetries?: number; initialDelayMs?: number; maxDelayMs?: number }): Promise<boolean>;
  ensureUserId(options?: EnsureUserIdOptions): Promise<boolean>;
}

export const AuthCookieService: AuthCookieServiceProps = {
  getLegacyUserId() {
    const legacyUserId = FrontendUserCookieService.getLegacyUserId();
    if (!legacyUserId) return 0;
    return legacyUserId;
  },

  getUserId() {
    const frontendUserId = FrontendUserCookieService.getUserId();
    if (!frontendUserId) return null;
    return frontendUserId;
  },

  getAccountId() {
    const frontendUserCookieAccountId = FrontendUserCookieService.getAccountId();
    if (!frontendUserCookieAccountId) return 0;
    return frontendUserCookieAccountId;
  },

  async waitForUserId(options = {}) {
    const { maxRetries = 5, initialDelayMs = 1000, maxDelayMs } = options;

    // getUserId() reads frontend_user_token (set by the backend); fetching a new
    // JWT has no effect on that cookie, so there's nothing to trigger here besides
    // waiting for the cookie to show up.
    const success = await retryWithBackoff(
      async () => this.getUserId(),
      (id) => !id || id === '',
      { maxRetries, initialDelayMs, maxDelayMs }
    );

    return success;
  },

  async ensureUserId(options: EnsureUserIdOptions = {}): Promise<boolean> {
    const { maxRetries, initialDelayMs, maxDelayMs, onRetrying } = options;

    const userId = this.getUserId();
    if (userId && userId !== '') {
      return true;
    }

    onRetrying?.(true);
    try {
      return await this.waitForUserId({ maxRetries, initialDelayMs, maxDelayMs });
    } finally {
      onRetrying?.(false);
    }
  },
};

async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  shouldRetry: (result: T) => boolean,
  options: RetryOptions = {}
): Promise<boolean> {
  const { maxRetries = 5, initialDelayMs = 1000, maxDelayMs = 16000 } = options;

  let attempt = 0;
  let delay = initialDelayMs;

  while (attempt <= maxRetries) {
    try {
      const result = await fn();
      if (!shouldRetry(result)) {
        return true;
      }
    } catch {
      // treat thrown exceptions as a failed attempt, retry if budget remains
    }

    if (attempt === maxRetries) {
      return false;
    }

    await new Promise((resolve) => setTimeout(resolve, delay));
    delay = Math.min(delay * 2, maxDelayMs);
    attempt++;
  }

  return false;
}
